import PositionService from '../services/PositionService';

/**
 * Wrapper pour envoyer des actions via WebSocket avec fallback HTTP
 * 
 * Usage:
 *   const result = await wsAction.updatePosition(wsSend, {
 *     position_id: 123,
 *     x: 0.5,
 *     y: 0.3
 *   }, user.access_token);
 */

class WebSocketActionWrapper {
  constructor() {
    this.pendingRequests = new Map(); // Pour tracker les requêtes en attente
  }

  /**
   * Envoie une action via WebSocket avec fallback HTTP
   * @param {Function} wsSend - Fonction d'envoi WebSocket
   * @param {string} messageType - Type de message (ex: 'position_update')
   * @param {object} data - Données du message
   * @param {Function} httpFallback - Fonction HTTP de fallback
   * @param {number} timeout - Timeout en ms (défaut: 5000)
   * @returns {Promise} Résultat de l'action
   */
  async sendAction(wsSend, messageType, data, httpFallback, timeout = 5000) {
    // Générer un ID unique pour cette requête
    const requestId = `${messageType}_${Date.now()}_${Math.random()}`;
    
    return new Promise((resolve, reject) => {
      // Si WebSocket non disponible, fallback direct
      if (!wsSend || typeof wsSend !== 'function') {
        console.log(`⚠️ WebSocket non disponible, fallback HTTP pour ${messageType}`);
        return httpFallback()
          .then(resolve)
          .catch(reject);
      }

      // Timeout pour fallback automatique
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        console.log(`⏱️ Timeout WebSocket pour ${messageType}, fallback HTTP`);
        httpFallback()
          .then(resolve)
          .catch(reject);
      }, timeout);

      // Stocker la requête en attente
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeoutId,
        messageType
      });

      // Envoyer via WebSocket
      const wsMessage = {
        ...data,
        type: messageType,
        request_id: requestId // Pour matcher la réponse
      };

      try {
        const sent = wsSend(wsMessage);
        if (!sent) {
          // Échec d'envoi WebSocket, fallback direct
          clearTimeout(timeoutId);
          this.pendingRequests.delete(requestId);
          console.log(`❌ Échec envoi WebSocket pour ${messageType}, fallback HTTP`);
          httpFallback()
            .then(resolve)
            .catch(reject);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        this.pendingRequests.delete(requestId);
        console.log(`❌ Erreur envoi WebSocket pour ${messageType}:`, error);
        httpFallback()
          .then(resolve)
          .catch(reject);
      }
    });
  }

  /**
   * Traite une réponse WebSocket
   * @param {object} message - Message reçu du serveur
   */
  handleResponse(message) {
    const requestId = message.request_id;
    if (!requestId) return false; // Pas une réponse à une requête

    const pending = this.pendingRequests.get(requestId);
    if (!pending) return false; // Requête déjà traitée ou inconnue

    clearTimeout(pending.timeoutId);
    this.pendingRequests.delete(requestId);

    if (message.type === 'error') {
      pending.reject(new Error(message.message));
    } else {
      pending.resolve(message);
    }

    return true; // Réponse traitée
  }

  // ==================== POSITIONS ====================

  async updatePosition(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'position_update',
      data,
      () => PositionService.updatePosition(data.position_id, data, accessToken)
    );
  }

  async createPosition(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'position_create',
      data,
      () => PositionService.createPosition(data, accessToken)
    );
  }

  async deletePosition(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'position_delete',
      data,
      () => PositionService.deletePosition(data.position_id, accessToken)
    );
  }

  async assignDancer(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'position_assign_dancer',
      data,
      () => PositionService.updatePosition(data.position_id, { dancer_id: data.dancer_id }, accessToken)
    );
  }

  // ==================== DANCERS ====================

  async createDancer(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'dancer_create',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${data.session_id}/dancers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ name: data.name })
      }).then(r => r.json())
    );
  }

  async updateDancer(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'dancer_update',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/dancers/${data.dancer_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(data)
      }).then(r => r.json())
    );
  }

  async deleteDancer(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'dancer_delete',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/dancers/${data.dancer_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  // ==================== SKILLS ====================

  async createSkill(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'skill_create',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${data.session_id}/skills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ name: data.name, color: data.color })
      }).then(r => r.json())
    );
  }

  async deleteSkill(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'skill_delete',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/skills/${data.skill_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  // ==================== PERIODS ====================

  async createPeriod(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'period_create',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${data.session_id}/periods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ 
          name: data.name,
          start_time: data.start_time,
          end_time: data.end_time
        })
      }).then(r => r.json())
    );
  }

  async updatePeriod(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'period_update',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/periods/${data.period_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(data)
      }).then(r => r.json())
    );
  }

  async deletePeriod(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'period_delete',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/periods/${data.period_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  // ==================== ASSOCIATIONS SKILLS ====================

  async associateSkillToPosition(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'position_associate_skill',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${data.session_id}/positions/${data.position_id}/skills/${data.skill_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  async removeSkillFromPosition(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'position_remove_skill',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${data.session_id}/positions/${data.position_id}/skills/${data.skill_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  async associateSkillToDancer(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'dancer_associate_skill',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${data.session_id}/dancers/${data.dancer_id}/skills/${data.skill_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  async removeSkillFromDancer(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'dancer_remove_skill',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${data.session_id}/dancers/${data.dancer_id}/skills/${data.skill_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  // ==================== ASSOCIATIONS STUNTS ====================

  async associateStuntToPosition(wsSend, data, accessToken) {
    // Construction de l'URL avec ou sans stunt_instance_id
    const url = new URL(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/positions/${data.position_id}/stunts/${data.stunt_id}`);
    if (data.stunt_instance_id) {
      url.searchParams.append('stunt_instance_id', data.stunt_instance_id);
    }
    
    return this.sendAction(
      wsSend,
      'position_associate_stunt',
      data,
      () => fetch(url.toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }
  
  async getStuntInstances(sessionId, periodId, stuntId, accessToken) {
    return fetch(`${process.env.REACT_APP_API_URL}/sessions/${sessionId}/periods/${periodId}/stunts/${stuntId}/instances`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }).then(r => r.json());
  }

  async removeStuntFromPosition(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'position_remove_stunt',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/positions/${data.position_id}/stunts/${data.stunt_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  // ==================== LINKS ====================

  async createLinkGroup(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'link_create',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/links`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ position_ids: data.position_ids })
      }).then(r => r.json())
    );
  }

  async deleteLinkGroup(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'link_delete',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/links/${data.link_group_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  async unlinkPosition(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'link_unlink_position',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/positions/${data.position_id}/unlink`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  // ==================== STUNTS CRUD ====================

  async createStunt(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_create',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: data.name, color: data.color })
      }).then(r => r.json())
    );
  }

  async updateStunt(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_update',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunts/${data.stunt_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: data.name })
      }).then(r => r.json())
    );
  }

  async deleteStunt(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_delete',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunts/${data.stunt_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  // ==================== STUNT GROUPS CRUD ====================

  async createStuntGroup(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_group_create',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunt_groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: data.name })
      }).then(r => r.json())
    );
  }

  async updateStuntGroup(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_group_update',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunt_groups/${data.group_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: data.name })
      }).then(r => r.json())
    );
  }

  async deleteStuntGroup(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_group_delete',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunt_groups/${data.group_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }).then(r => r.json())
    );
  }

  async addDancersToStuntGroup(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_group_add_dancers',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunt_groups/${data.group_id}/dancers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dancer_ids: data.dancer_ids })
      }).then(r => r.json())
    );
  }

  async addStuntsToStuntGroup(wsSend, data, accessToken) {
    return this.sendAction(
      wsSend,
      'stunt_group_add_stunts',
      data,
      () => fetch(`${process.env.REACT_APP_API_URL}/sessions/${data.session_id}/stunt_groups/${data.group_id}/stunts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stunt_ids: data.stunt_ids })
      }).then(r => r.json())
    );
  }
}

// Instance singleton
const wsAction = new WebSocketActionWrapper();

export default wsAction;
